import { getCurrentLanguage, interpolate, applyTranslationsToContainer } from '../../../translations.js';
import {
  clearCharacterInfoOverride,
  getCharacterInfoOverride,
  getEffectiveCharacterInfo,
  setCharacterInfoOverride
} from '../hangout-memory-storage.js';
import { estimateTokens, formatTokenCount } from '../token-estimate.js';
import { createElement } from './settings-dom-utils.js';

const INFO_SKIP_KEYS = new Set(['race', 'lora_activation']);

const INFO_LONG_KEYS = new Set(['description', 'message_example', 'example']);

const LONG_VALUE_THRESHOLD = 20;

const INFO_KEY_TO_TRANSLATE = {
  age: 'character_modal.age',
  height: 'character_modal.height',
  ethnicity: 'character_modal.ethnicity',
  occupation: 'character_modal.occupation',
  birthdate: 'character_modal.birthdate',
  favorite_spot: 'character_modal.favorite_spot',
  description: 'character_modal.character_description',
  message_example: 'character_modal.example_dialogue',
  example: 'character_modal.example_dialogue',
  likes: 'character_modal.likes',
  dislikes: 'character_modal.dislikes'
};

const formatInfoLabel = (key) =>
  key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const getLabelDomId = (key) =>
  `label-info-${key.replace(/[^a-zA-Z0-9_-]/g, '-')}`;

const valueToDisplayString = (val) => {
  if (Array.isArray(val)) {
    return val
      .map(item => (typeof item === 'string' ? item : item?.name ?? item))
      .filter(v => v != null && String(v).trim() !== '')
      .map(v => String(v).trim())
      .join(', ');
  }
  return String(val);
};

const getFieldDisplayMeta = (key, val) => {
  const displayText = valueToDisplayString(val);
  const isLong = INFO_LONG_KEYS.has(key) || displayText.length > LONG_VALUE_THRESHOLD;
  return { displayText, isLong };
};

const getCharacterModalText = (key, fallback = '') => {
  const language = getCurrentLanguage();
  const fromLanguage = window.translations?.[language]?.hangout?.character_modal?.[key];
  const fromEnglish = window.translations?.en?.hangout?.character_modal?.[key];
  return fromLanguage ?? fromEnglish ?? fallback;
};

const originalDisplayForKey = (originalInfo, key) => {
  if (!Object.prototype.hasOwnProperty.call(originalInfo, key)) return '';
  const val = originalInfo[key];
  if (val === null || val === undefined || val === '') return '';
  if (typeof val === 'object' && !Array.isArray(val)) return '';
  if (Array.isArray(val) && val.length === 0) return '';
  return valueToDisplayString(val);
};

const isCharacterInfoAtDefault = (originalInfo, characterId) => {
  if (characterId == null) return true;
  const override = getCharacterInfoOverride(characterId);
  if (override.removed.length > 0) return false;
  for (const [key, value] of Object.entries(override.values)) {
    if (String(value) !== originalDisplayForKey(originalInfo, key)) return false;
  }
  return true;
};

const estimateInfoTokens = (info) => {
  const blob = Object.entries(info || {})
    .map(([key, value]) => `${key}: ${value}`)
    .join('\n');
  return estimateTokens(blob);
};

export const applyCharacterInfoDynamicTranslations = (container) => {
  if (!container?.querySelectorAll) return;
  container.querySelectorAll('[data-info-fields-about]').forEach((heading) => {
    const name = heading.dataset.name || 'Character';
    const template = getCharacterModalText('about_character', heading.textContent || 'About {name}');
    heading.textContent = interpolate(template, { name });
  });
};

export function appendCharacterInfoFields(info, characterDetails, options = {}) {
  const originalInfo = info && typeof info === 'object' ? info : {};
  const characterId = options.characterId;
  const characterName = options.characterName || 'Character';

  const wrap = createElement('div', 'info-fields-wrap');

  const syncResetButtonVisibility = (resetBtn) => {
    const hide = isCharacterInfoAtDefault(originalInfo, characterId);
    resetBtn.hidden = hide;
    resetBtn.setAttribute('aria-hidden', hide ? 'true' : 'false');
  };

  const renderGrid = () => {
    wrap.innerHTML = '';

    const override = characterId != null
      ? getCharacterInfoOverride(characterId)
      : { values: {}, removed: [] };
    const removed = new Set(override.removed);
    const effective = characterId != null
      ? getEffectiveCharacterInfo(originalInfo, characterId)
      : Object.fromEntries(
        Object.entries(originalInfo)
          .filter(([key, val]) => {
            if (INFO_SKIP_KEYS.has(key)) return false;
            if (val === null || val === undefined || val === '') return false;
            if (Array.isArray(val) && val.length === 0) return false;
            if (typeof val === 'object' && !Array.isArray(val)) return false;
            return true;
          })
          .map(([key, val]) => [key, valueToDisplayString(val)])
      );

    const orderedFields = Object.entries(effective)
      .filter(([key]) => !INFO_SKIP_KEYS.has(key) && !removed.has(key))
      .map(([key, val]) => {
        const { displayText, isLong } = getFieldDisplayMeta(key, val);
        return { key, displayText, isLong };
      })
      .sort((a, b) => Number(a.isLong) - Number(b.isLong));

    const grid = createElement('div', 'info-fields-grid');

    const header = createElement('div', 'info-fields-grid-header');
    const heading = createElement('h3', 'info-fields-about');
    heading.setAttribute('data-translate', 'character_modal.about_character');
    heading.setAttribute('data-info-fields-about', 'true');
    heading.dataset.name = characterName;

    const resetBtn = createElement('button', 'selection-btn info-fields-reset', {
      type: 'button',
      textContent: getCharacterModalText('reset_info', 'Reset')
    });
    resetBtn.setAttribute('data-translate', 'character_modal.reset_info');
    resetBtn.setAttribute('aria-label', resetBtn.textContent);
    resetBtn.addEventListener('click', (event) => {
      event.preventDefault();
      if (characterId != null) clearCharacterInfoOverride(characterId);
      renderGrid();
    });
    syncResetButtonVisibility(resetBtn);

    header.appendChild(heading);
    header.appendChild(resetBtn);
    grid.appendChild(header);

    const tokenEl = createElement('div', 'info-fields-token-count');

    for (const { key, displayText, isLong } of orderedFields) {
      const group = createElement('div', `form-group info-field ${isLong ? 'info-field--long' : 'info-field--short'}`);
      const labelId = getLabelDomId(key);
      group.setAttribute('aria-labelledby', labelId);

      const removeBtn = createElement('button', 'info-field-remove', {
        type: 'button',
        textContent: '×'
      });
      removeBtn.setAttribute('data-translate-attr', 'aria-label:character_modal.remove_field');
      removeBtn.setAttribute('aria-label', getCharacterModalText('remove_field', 'Remove field'));
      removeBtn.addEventListener('click', (event) => {
        event.preventDefault();
        if (characterId == null) return;
        const current = getCharacterInfoOverride(characterId);
        const nextValues = { ...current.values };
        delete nextValues[key];
        setCharacterInfoOverride(characterId, {
          values: nextValues,
          removed: [...current.removed, key]
        });
        renderGrid();
      });

      const lbl = createElement('label', '', { id: labelId, textContent: formatInfoLabel(key) });
      const translateKey = INFO_KEY_TO_TRANSLATE[key];
      if (translateKey) {
        lbl.setAttribute('data-translate', translateKey);
      }

      const valueEl = isLong
        ? createElement('textarea', 'value info-value--long', { rows: 4 })
        : createElement('input', 'value info-value--short', { type: 'text' });
      valueEl.value = displayText;
      valueEl.setAttribute('aria-labelledby', labelId);
      valueEl.addEventListener('input', () => {
        if (characterId == null) return;
        const current = getCharacterInfoOverride(characterId);
        setCharacterInfoOverride(characterId, {
          values: { ...current.values, [key]: valueEl.value },
          removed: current.removed
        });
        tokenEl.textContent = formatTokenCount(estimateInfoTokens(getEffectiveCharacterInfo(originalInfo, characterId)));
        syncResetButtonVisibility(resetBtn);
      });

      group.appendChild(removeBtn);
      group.appendChild(lbl);
      group.appendChild(valueEl);
      grid.appendChild(group);
    }

    tokenEl.textContent = formatTokenCount(estimateInfoTokens(effective));
    grid.appendChild(tokenEl);

    wrap.appendChild(grid);
    applyTranslationsToContainer(wrap, 'hangout', getCurrentLanguage());
    applyCharacterInfoDynamicTranslations(wrap);
  };

  renderGrid();
  characterDetails.appendChild(wrap);
}
